/**
 * Angular-free utilities for the reporting/export layer (reporting-core
 * boundary, see `README.md`): no `@angular/*` or browser globals as runtime
 * values; `import type` is fine. `src/app/utils.ts` re-exports these.
 */
import * as moment from 'moment-timezone';
import type { ParamMap } from '@angular/router';
import { RelativeDateRange } from '../models/date-range-filter';

/** Whether the value is neither null nor undefined. */
export function propExists(prop: any) {
  return (prop !== null) && (prop !== undefined);
}

export function propExistsAndIsNonEmpty(prop: any) {
  return (
    propExists(prop) &&
    (
      (prop.length === undefined) ||
      (prop.length > 0)
    )
  );
}

export function capitalizeFirstLetter(inputStr: string) {
  return inputStr.charAt(0).toUpperCase() + inputStr.slice(1);
}

export function daysAgoText(numDays: number): string {
  return numDays === 1 ? '1 day ago' : numDays.toString() + ' days ago';
}

export function getDaysAndMonthsString(numDays: number | null,
                                       numMonths: number | null): string {
  const timeParts: string[] = [];

  if (propExists(numMonths)) {
    timeParts.push(numMonths.toString() + ' month' + (numMonths !== 1 ? 's' : ''));
  }
  if (propExists(numDays)) {
    timeParts.push(numDays.toString() + ' day' + (numDays !== 1 ? 's' : ''));
  }

  return timeParts.join(' and ');
}

export function getStaffNameDisplay(firstName: string | null,
                                    lastName: string | null,
                                    email: string | null): string {
  if (!firstName && !lastName && !email) {
    return '(no name)';
  }
  if (!firstName && !lastName) {
    return email;
  }
  let nameDisplay = firstName + ' ' + lastName;
  nameDisplay = nameDisplay.trim();
  if (email) {
    nameDisplay += ' (' + email + ')';
  }
  return nameDisplay.trim();
}

/** An iso8601 datetime (or date) string as a locally-formatted date. */
export function dateStringFromISO8601(iso8601datetime: string, timezone: string | null = null) {
  // A date-only value is already a localized calendar date; re-zoning it on the
  // UTC emailed-reports host rolls it back a day.
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(iso8601datetime);
  let parsedDt = moment(iso8601datetime);
  if (timezone !== null && !isDateOnly) { parsedDt = parsedDt.tz(timezone); }
  return parsedDt.format('ll');
}

/**
 * Run `fn` with moment's default zone set to `timezone` (null = the runtime's
 * own), restoring it afterwards. The emailed-reports service's hook for export
 * columns that format a datetime with no explicit zone (`getContentItem` takes
 * none): it must live HERE, on the same moment-timezone instance the
 * formatters use — the service resolves its own copy of the package, and
 * setting the default on that copy does nothing to this one (PRO-1499).
 */
export function withDefaultTimezone<T>(timezone: string | null, fn: () => T): T {
  // `defaultZone` isn't in moment-timezone's typings.
  const previousZone = (moment as any).defaultZone;
  const previous: string | undefined = previousZone ? previousZone.name : undefined;
  moment.tz.setDefault(timezone || undefined);
  try {
    return fn();
  } finally {
    moment.tz.setDefault(previous);
  }
}

export function timeStringFromISO8601(iso8601datetime: string, timezone: string | null = null) {
  let parsedDt = moment(iso8601datetime);
  if (timezone !== null) { parsedDt = parsedDt.tz(timezone); }
  return parsedDt.format('LT');
}

export function loadStringFilterFromGetParams(filters: any, paramName: string, queryParamMap: ParamMap) {
  if (queryParamMap.get(paramName)) {
    filters[paramName] = queryParamMap.get(paramName);
  }
}

export function loadIntFilterFromGetParams(filters: any, paramName: string, queryParamMap: ParamMap) {
  if (queryParamMap.get(paramName)) {
    const parsedVal = parseInt(queryParamMap.get(paramName), 10);
    if (!isNaN(parsedVal)) {
      filters[paramName] = parsedVal;
    }
  }
}

/**
 * Load a list filter from the URL. Reads EVERY occurrence of the param
 * (Angular and server-built links write `?x=a&x=b`; `ParamMap.get` would keep
 * only the first) as well as comma-joined values. With `allowedValues`, entries
 * are matched case-insensitively and unrecognised ones DROPPED — the filtersets
 * 400 on an unknown value, and a stale link should widen, not error.
 */
export function loadStringListFilterFromGetParams(filters: any,
                                                  paramName: string,
                                                  queryParamMap: ParamMap,
                                                  allowedValues: string[] = null) {
  const rawValues = queryParamMap.getAll(paramName);
  if (rawValues.length === 0) { return; }

  let values = rawValues
    .reduce((tokens, raw) => tokens.concat(raw.split(',')), [] as string[])
    .map(value => value.trim())
    .filter(value => value !== '');
  if (allowedValues !== null) {
    values = values
      .map(value => allowedValues.find(
        allowed => allowed.toLowerCase() === value.toLowerCase()
      ))
      .filter(value => value !== undefined);
  }
  // De-duplicate, so a repeated entry can't survive into a saved view.
  values = values.filter((value, index) => values.indexOf(value) === index);

  if (values.length > 0) {
    filters[paramName] = values;
  }
}

export function loadFloatFilterFromGetParams(filters: any, paramName: string, queryParamMap: ParamMap) {
  if (queryParamMap.get(paramName)) {
    const parsedVal = parseFloat(queryParamMap.get(paramName));
    if (!isNaN(parsedVal)) {
      filters[paramName] = parsedVal;
    }
  }
}

export function loadBoolFilterFromGetParams(filters: any, paramName: string, queryParamMap: ParamMap) {
  if (typeof queryParamMap.get(paramName) === 'string') {
    const paramVal = queryParamMap.get(paramName);
    switch (paramVal.toLowerCase()) {
      case 'true':
        filters[paramName] = true;
        break;
      case 'false':
        filters[paramName] = false;
        break;
      case '':
        filters[paramName] = undefined;
        break;
    }
  }
}

/**
 * The Date as an ISO-8601 date string in the process-local time zone. Not for
 * an org-zoned moment — see `getRelativeDateRangeBounds`.
 */
export function getISO8601DateString(date: Date) {
  return [
    date.getFullYear().toString(),
    (date.getMonth() + 1).toString().padStart(2, '0'),
    date.getDate().toString().padStart(2, '0')
  ].join('-');
}

/**
 * Resolve a relative date-range key ("last_month") to the inclusive
 * `{ startDateStr, endDateStr }` it covers in `today`'s time zone, or null for
 * an unknown key. `today` is passed in so the emailed-report renderer can
 * resolve org-side (`moment.tz(orgTimeZone)`). Mirrors the backend
 * `get_relative_date_range_bounds` (`apps/capitan_server/date_range_filters.py`)
 * so a chart and a tabular report cover the same dates.
 */
export function getRelativeDateRangeBounds(
  rangeKey: string,
  today: moment.Moment,
): { startDateStr: string; endDateStr: string } | null {
  const t = today.clone().startOf('day');
  let start: moment.Moment;
  let end: moment.Moment;

  switch (rangeKey) {
    case RelativeDateRange.today:
    // Charts bucket by date, so "last 24 hours" collapses to today's bucket.
    case RelativeDateRange.last24Hours:
      start = t.clone();
      end = t.clone();
      break;
    case RelativeDateRange.yesterday:
      start = t.clone().subtract(1, 'days');
      end = start.clone();
      break;
    case RelativeDateRange.last7Days:
      start = t.clone().subtract(6, 'days');
      end = t.clone();
      break;
    case RelativeDateRange.last28Days:
      start = t.clone().subtract(27, 'days');
      end = t.clone();
      break;
    case RelativeDateRange.last31Days:
      start = t.clone().subtract(30, 'days');
      end = t.clone();
      break;
    case RelativeDateRange.last90Days:
      start = t.clone().subtract(89, 'days');
      end = t.clone();
      break;
    case RelativeDateRange.last365Days:
      start = t.clone().subtract(364, 'days');
      end = t.clone();
      break;
    case RelativeDateRange.thisWeekStartingMonday:
      start = t.clone().subtract(t.isoWeekday() - 1, 'days');
      end = start.clone().add(6, 'days');
      break;
    case RelativeDateRange.thisWeekStartingSunday:
      start = t.clone().subtract(t.isoWeekday() % 7, 'days');
      end = start.clone().add(6, 'days');
      break;
    case RelativeDateRange.lastWeekStartingMonday:
      start = t.clone().subtract(t.isoWeekday() - 1, 'days').subtract(7, 'days');
      end = start.clone().add(6, 'days');
      break;
    case RelativeDateRange.lastWeekStartingSunday:
      start = t.clone().subtract(t.isoWeekday() % 7, 'days').subtract(7, 'days');
      end = start.clone().add(6, 'days');
      break;
    case RelativeDateRange.thisMonth:
      start = t.clone().startOf('month');
      end = start.clone().add(1, 'months').subtract(1, 'days');
      break;
    case RelativeDateRange.lastMonth:
      start = t.clone().startOf('month').subtract(1, 'months');
      end = t.clone().startOf('month').subtract(1, 'days');
      break;
    case RelativeDateRange.thisQuarter:
      start = t.clone().startOf('quarter');
      end = start.clone().add(3, 'months').subtract(1, 'days');
      break;
    case RelativeDateRange.lastQuarter:
      start = t.clone().startOf('quarter').subtract(3, 'months');
      end = t.clone().startOf('quarter').subtract(1, 'days');
      break;
    case RelativeDateRange.thisYear:
      start = t.clone().startOf('year');
      end = t.clone().endOf('year').startOf('day');
      break;
    case RelativeDateRange.yearToDate:
      // Stops at the current day, so a chart plots no future periods (PRO-1370).
      start = t.clone().startOf('year');
      end = t.clone();
      break;
    case RelativeDateRange.lastYear:
      start = t.clone().startOf('year').subtract(1, 'years');
      end = t.clone().startOf('year').subtract(1, 'days');
      break;
    case RelativeDateRange.next7Days:
      start = t.clone();
      end = t.clone().add(6, 'days');
      break;
    case RelativeDateRange.next30Days:
      start = t.clone();
      end = t.clone().add(29, 'days');
      break;
    case RelativeDateRange.next90Days:
      start = t.clone();
      end = t.clone().add(89, 'days');
      break;
    case RelativeDateRange.nextMonth:
      start = t.clone().startOf('month').add(1, 'months');
      end = start.clone().add(1, 'months').subtract(1, 'days');
      break;
    case RelativeDateRange.nextQuarter:
      start = t.clone().startOf('quarter').add(3, 'months');
      end = start.clone().add(3, 'months').subtract(1, 'days');
      break;
    default:
      return null;
  }

  // `.format`, NOT `getISO8601DateString(start.toDate())`: the latter reads
  // process-local Date getters and shifts an org-zoned moment across midnight.
  return {
    startDateStr: start.format('YYYY-MM-DD'),
    endDateStr: end.format('YYYY-MM-DD'),
  };
}

/**
 * The absolute start/end a chart report queries for: a fixed min/max wins,
 * else the relative range resolved against the browser's local "now" (the
 * backend's precedence). A caller with a non-browser "now" (the emailed
 * renderer) calls `getRelativeDateRangeBounds` with its own `today` instead.
 */
export function resolveDateRangeBounds(
  min: string | undefined,
  max: string | undefined,
  range: string | undefined,
): { startDateStr: string | undefined; endDateStr: string | undefined } {
  if (!min && !max && range) {
    const bounds = getRelativeDateRangeBounds(range, moment());
    if (bounds !== null) {
      return { startDateStr: bounds.startDateStr, endDateStr: bounds.endDateStr };
    }
  }
  return { startDateStr: min, endDateStr: max };
}

/**
 * The Staff Site path to an event's details page, nested under the
 * scheduled-events bucket the event falls into at render time (upcoming / now /
 * past), which is where the page's back arrow returns to.
 */
export function getEventDetailsLink(startDatetime: string,
                                    endDatetime: string,
                                    startDateLocal: string,
                                    eventPartId: number): string {
  let link = '/events/scheduled/';

  if (moment(startDatetime).isSameOrAfter(moment())) {
    link += 'upcoming/';
    link += startDateLocal;
    link += '/';
  } else if (moment(endDatetime).isSameOrAfter(moment())) {
    link += 'now/';
  } else {
    link += 'past/';
    link += startDateLocal;
    link += '/';
  }

  link += 'event/' + eventPartId.toString();

  return link;
}
